import apiClient from './apiClient';

export interface EvaluationItem {
  id?: number;
  evaluation_id?: number;
  sort_order: number;
  dfp_sku?: string | null;
  description?: string | null;
  msrp?: number | null;
  current_price?: number | null;
  recommended_price?: number | null;
  dfp_cost?: number | null;
  dfp_unit_gm?: number | null;
  competitor?: string | null;
  comp_sku?: string | null;
  comp_price?: number | null;
  est_quantity?: number | null;
  requested_price?: number | null;
  approved_price?: number | null;
  item_note?: string | null;
  ext_price?: number | null;
  ext_cost?: number | null;
}

export interface EvaluationListItem {
  id: number;
  evaluation_number: string;
  company_name: string;
  company_contact_name?: string | null;
  sales_person_name: string;
  approver_id: number;
  approver_name: string;
  status: string;
  created_at?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
  total_ext_price?: number | null;
  item_count: number;
}

export interface Evaluation {
  id: number;
  evaluation_number: string;
  sales_person_id: number;
  sales_person_name: string;
  approver_id: number;
  approver_name: string;
  company_id: number;
  company_name: string;
  company_pricing_tier: number;
  company_contact_id?: number | null;
  company_contact_name?: string | null;
  opportunity_description?: string | null;
  status: string;
  submitted_at?: string | null;
  approved_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  items: EvaluationItem[];
  total_ext_price?: number | null;
  total_ext_cost?: number | null;
  overall_gm?: number | null;
}

export interface ApprovalItemUpdate {
  id: number;
  approved_price?: number | null;
  item_note?: string | null;
}

export interface ApprovedPriceHistoryItem {
  approved_at?: string | null;
  approved_price: number;
  company_name: string;
  company_tier: number;
  sales_person_name: string;
}

export interface RequestedPriceHistoryItem {
  requested_at?: string | null;
  requested_price: number;
  company_name: string;
  company_tier: number;
  sales_person_name: string;
  status: string;
}

export interface DashboardData {
  kpis: {
    last_30d: { total: number; pending: number; approved: number; rejected: number };
    year: { total: number; pending: number; approved: number; rejected: number };
  };
  evals_per_sales: { name: string; count: number }[];
  amount_per_sales: { name: string; amount: number }[];
  monthly_data: { name: string; year: number; month: number; amount: number }[];
}

const evaluationsApi = {
  dashboard: () =>
    apiClient.get<DashboardData>('/evaluations/dashboard').then(r => r.data),

  list: (status?: string) =>
    apiClient.get<EvaluationListItem[]>('/evaluations', { params: status ? { status } : {} }).then(r => r.data),

  get: (id: number) =>
    apiClient.get<Evaluation>(`/evaluations/${id}`).then(r => r.data),

  create: (data: { company_id: number; company_contact_id?: number; opportunity_description?: string; items: Partial<EvaluationItem>[] }) =>
    apiClient.post<Evaluation>('/evaluations', data).then(r => r.data),

  update: (id: number, data: { company_id?: number; company_contact_id?: number; opportunity_description?: string; items?: Partial<EvaluationItem>[] }) =>
    apiClient.put<Evaluation>(`/evaluations/${id}`, data).then(r => r.data),

  submit: (id: number) =>
    apiClient.post<Evaluation>(`/evaluations/${id}/submit`).then(r => r.data),

  approve: (id: number, items: ApprovalItemUpdate[]) =>
    apiClient.post<Evaluation>(`/evaluations/${id}/approve`, { items }).then(r => r.data),

  reject: (id: number, items: ApprovalItemUpdate[]) =>
    apiClient.post<Evaluation>(`/evaluations/${id}/reject`, { items }).then(r => r.data),

  getApprovedHistory: (sku: string, limit = 20) =>
    apiClient.get<ApprovedPriceHistoryItem[]>('/evaluations/item-history/approved', { params: { sku, limit } }).then(r => r.data),

  getRequestedHistory: (sku: string, limit = 20) =>
    apiClient.get<RequestedPriceHistoryItem[]>('/evaluations/item-history/requested', { params: { sku, limit } }).then(r => r.data),
};

export default evaluationsApi;
